import { useMemo, useState } from 'react';
import { adminApi } from '../../api/admin';
import { useAsync, Banner, Item, Empty } from './ui';
import { Modal } from '../../components/Modal';
import QrCode, { qrDataUrl } from '../../components/QrCode';
import { pmsGroupLabel, groupBy } from '../../lib/pmsGroup';

const baseUrl = window.location.origin;
const blank = { code: '', equipmentId: '', active: true, note: '', replacedBy: '' };
const isYes = (v: unknown) => v === true || String(v).toLowerCase() === 'true';

export default function TagsAdmin() {
  const { data, error, loading, reload } = useAsync(() => adminApi.listTags(baseUrl), [], 'tags');
  const equip = useAsync(() => adminApi.listEquipment(), [], 'equipment');
  const templates = useAsync(() => adminApi.listTemplates(), [], 'templates');

  const tplTitleById = useMemo(() => new Map(templates.data?.templates.map((t) => [t.templateId, t.title])), [templates.data]);
  const dailyEquip = useMemo(() => (equip.data?.rows ?? []).filter((r) => String(r['Category'] ?? 'daily') !== 'pms'), [equip.data]);
  const pmsEquipGroups = useMemo(() => {
    const pms = (equip.data?.rows ?? []).filter((r) => String(r['Category'] ?? 'daily') === 'pms');
    return groupBy(pms, (r) => pmsGroupLabel(tplTitleById.get(String(r['Template ID'])) ?? String(r['Template ID'])) || 'Other');
  }, [equip.data, tplTitleById]);
  const [form, setForm] = useState<typeof blank | null>(null);
  const [qr, setQr] = useState<{ code: string; url: string } | null>(null);
  const [write, setWrite] = useState<{ code: string; url: string } | null>(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form) return;
    setSaving(true); setMsg('');
    try { await adminApi.saveTag(form); setForm(null); reload(); }
    catch (e) { setMsg(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  const nameFor = (id: string) => String(equip.data?.rows.find((r) => String(r['Equipment ID']) === id)?.['Equipment Name'] ?? id);
  const urlFor = (r: Record<string, unknown>) => String(r['url'] ?? `${baseUrl}/t/${r['Tag Code']}`);

  async function downloadQr() {
    if (!qr) return;
    const png = await qrDataUrl(qr.url, 1000);
    const a = document.createElement('a');
    a.href = png; a.download = `QR_${qr.code}.png`; a.click();
  }

  return (
    <div>
      <div className="spread">
        <h1>NFC Tags</h1>
        <button className="sm" onClick={() => { setForm({ ...blank }); setMsg(''); }}>+ Register</button>
      </div>
      <p className="muted">
        Write each tag as one NDEF URL record. Scanning it — by NFC or by the <strong>QR code</strong> —
        opens the app at that equipment.
      </p>
      <Banner error={error || msg} />
      {loading && <div className="spinner" />}
      {data && data.rows.length === 0 && <Empty icon="🏷️">No tags registered.</Empty>}

      <div className="cardlist">
        {data?.rows.map((r, i) => (
          <Item
            key={i}
            stripe={isYes(r['Active']) ? 'ok' : 'bad'}
            title={<>{String(r['Tag Code'])} <span className="muted" style={{ fontWeight: 400 }}>→ {nameFor(String(r['Equipment ID']))}</span></>}
            meta={<>
              {!isYes(r['Active']) && <span style={{ color: 'var(--bad)' }}>inactive</span>}
              {r['Note'] ? <span>{String(r['Note'])}</span> : null}
            </>}
            actions={<>
              <button className="secondary xs" onClick={() => setQr({ code: String(r['Tag Code']), url: urlFor(r) })}>QR</button>
              <button className="secondary xs" onClick={() => setWrite({ code: String(r['Tag Code']), url: urlFor(r) })}>✍️ Write</button>
              <button className="secondary xs" onClick={() => setForm({
                code: String(r['Tag Code'] ?? ''), equipmentId: String(r['Equipment ID'] ?? ''),
                active: isYes(r['Active']), note: String(r['Note'] ?? ''), replacedBy: String(r['Replaced By'] ?? ''),
              })}>Edit</button>
            </>}
          >
            <div className="muted tiny" style={{ marginTop: 8, wordBreak: 'break-all', userSelect: 'all' }}>{urlFor(r)}</div>
          </Item>
        ))}
      </div>

      {qr && (
        <Modal
          title={`Tag ${qr.code}`}
          onClose={() => setQr(null)}
          footer={<>
            <button className="secondary" onClick={() => window.print()}>🖨 Print</button>
            <button onClick={downloadQr}>⬇ Download PNG</button>
          </>}
        >
          <div className="center">
            <p className="muted tiny">Scan with any phone camera (or an NFC tap) to open the logbook for this equipment.</p>
            <div className="qrbox"><QrCode value={qr.url} size={300} /></div>
            <p className="tiny" style={{ wordBreak: 'break-all', userSelect: 'all' }}>{qr.url}</p>
          </div>
        </Modal>
      )}

      {write && <WriteTagModal code={write.code} url={write.url} onClose={() => setWrite(null)} />}

      {form && (
        <Modal
          title={form.code && data?.rows.find((r) => String(r['Tag Code']) === form.code) ? 'Edit / remap tag' : 'Register tag'}
          onClose={() => !saving && setForm(null)}
          footer={<>
            <button className="secondary" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
            <button onClick={save} disabled={saving || !form.code}>{saving ? 'Saving…' : 'Save'}</button>
          </>}
        >
          <label>Tag code</label>
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="BBB-002" />
          <label>Equipment</label>
          <select value={form.equipmentId} onChange={(e) => setForm({ ...form, equipmentId: e.target.value })}>
            <option value="">— select —</option>
            <optgroup label="Daily readings">
              {dailyEquip.map((r, i) => (
                <option key={i} value={String(r['Equipment ID'])}>{String(r['Equipment ID'])} — {String(r['Equipment Name'])}</option>
              ))}
            </optgroup>
            {pmsEquipGroups.map((g) => (
              <optgroup key={g.label} label={`PMS · ${g.label}`}>
                {g.items.map((r, i) => (
                  <option key={i} value={String(r['Equipment ID'])}>{String(r['Equipment ID'])} — {String(r['Equipment Name'])}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
            <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
          </label>
          <label>Note</label>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. main panel door" />
          {form.code && (
            <p className="muted tiny">URL to write: <code style={{ userSelect: 'all', wordBreak: 'break-all' }}>{baseUrl}/t/{form.code}</code></p>
          )}
        </Modal>
      )}
    </div>
  );
}

type NdefWriter = { write: (msg: { records: Array<{ recordType: 'url'; data: string }> }, opts?: { signal?: AbortSignal }) => Promise<void> };

/**
 * Writes the tag's URL directly from the browser via Web NFC (Chrome for Android only),
 * so tags always carry a plain NDEF URL record instead of whatever format a third-party
 * writer app defaults to (e.g. an Android Application Record, which opens the app but
 * discards the deep-link target).
 */
function WriteTagModal({ code, url, onClose }: { code: string; url: string; onClose: () => void }) {
  const [status, setStatus] = useState<'idle' | 'writing' | 'done' | 'error' | 'unsupported'>('idle');
  const [error, setError] = useState('');

  const NDEFReaderCtor = (window as unknown as { NDEFReader?: new () => NdefWriter }).NDEFReader;

  async function writeNow() {
    if (!NDEFReaderCtor) { setStatus('unsupported'); return; }
    setStatus('writing'); setError('');
    try {
      const writer = new NDEFReaderCtor();
      await writer.write({ records: [{ recordType: 'url', data: url }] });
      setStatus('done');
    } catch (e) {
      const name = (e as { name?: string })?.name;
      if (name === 'NotAllowedError') setError('NFC permission was denied. Allow NFC for this site in your browser/site settings.');
      else if (name === 'NotSupportedError') setError('This tag doesn\'t support writing, or NFC is off on this device.');
      else setError(e instanceof Error ? e.message : 'Write failed.');
      setStatus('error');
    }
  }

  return (
    <Modal
      title={`Write tag ${code}`}
      onClose={onClose}
      footer={<>
        <button className="secondary" onClick={onClose}>Close</button>
        {NDEFReaderCtor && status !== 'writing' && (
          <button onClick={writeNow}>{status === 'done' ? 'Write another tag' : 'Start writing'}</button>
        )}
      </>}
    >
      <div className="center">
        {!NDEFReaderCtor && (
          <p className="muted">
            In-app tag writing needs Web NFC, which only <strong>Chrome for Android</strong> supports.
            Open this admin panel in Chrome on an Android phone with NFC turned on, then come back to this tag.
          </p>
        )}
        {NDEFReaderCtor && status === 'idle' && (
          <p className="muted">Tap "Start writing", then hold a blank (or reusable) NFC tag against the back of the phone. This writes a single URL record — no third-party app needed.</p>
        )}
        {status === 'writing' && (
          <>
            <p className="scanSheet-title" style={{ margin: '4px 0' }}>Hold the tag against your phone…</p>
            <p className="muted tiny">Writing will happen automatically once contact is made.</p>
          </>
        )}
        {status === 'done' && <p style={{ color: 'var(--ok)', fontWeight: 600 }}>✓ Tag written successfully.</p>}
        {status === 'error' && <p style={{ color: 'var(--bad)' }}>{error}</p>}
        <p className="tiny muted" style={{ wordBreak: 'break-all', userSelect: 'all', marginTop: 14 }}>{url}</p>
      </div>
    </Modal>
  );
}
